import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiInstagramChat as apiChatTs } from '../../http';
import * as yup from 'yup';
import { toast } from 'react-toastify';

export const CHATS_BY_AGENT_LOGIN_SLICE_NAME = 'chatsByAgentLogin';
const ACTION_PREFIX_FETCH_BY_LOGIN = `${CHATS_BY_AGENT_LOGIN_SLICE_NAME}/fetchByLogin`;
const ACTION_PREFIX_FORCE_FETCH_BY_LOGIN = `${CHATS_BY_AGENT_LOGIN_SLICE_NAME}/forceFetchByLogin`;

const CHAT_IN_ATTENDANCE_SCHEMA = yup.object({
  id: yup.string().required(),
  atendida: yup.boolean().required(),
  situacao: yup
    .string()
    .oneOf(
      ['em_atendimento'],
      "Chats em atendimento precisam ter a situacao 'em_atendimento'"
    )
    .required(),
  fila: yup.string().required(),
  cliente: yup
    .object({
      id: yup.string(),
      email: yup.string().nullable(),
      telefone: yup.string().nullable(),
      nome: yup.string().required(),
      id_whatsapp: yup.string().nullable(),
      account: yup
        .object({
          idAccount: yup.number().nullable(),
          nameAccount: yup.string().nullable()
        })
        .nullable()
        .default(null),
      accounts: yup
        .array()
        .of(
          yup.object({
            id: yup.number().nullable(),
            name: yup.string().nullable()
          })
        )
        .nullable()
        .default(null)
    })
    .required(),
  hora_criacao: yup.string().required()
});

const GET_CHATS_IN_ATTENDANCE_BY_LOGIN_SCHEMA = yup.array(
  CHAT_IN_ATTENDANCE_SCHEMA
);

/**
 * Busca os chats em atendimento para um agente a partir do login e valida a resposta
 * @param {string} login
 */
const fetchAndValidateChats = async (login, rejectWithValue) => {
  try {
    const { data: responseData } = await apiChatTs.get(
      `/chat/in-attendance/agent/${login}`
    );

    const { success, error, data } =
      GET_CHATS_IN_ATTENDANCE_BY_LOGIN_SCHEMA.safeValidateSync(responseData);

    if (!success) {
      toast.error(
        error?.message ?? 'Não foi possível validar a resposta do servidor'
      );
      return rejectWithValue(error.message);
    }

    return { login, data };
  } catch (error) {
    console.error(
      `Falha ao buscar conversas para o atendente ${login}:`,
      error
    );
    return rejectWithValue(error.message || 'Erro desconhecido');
  }
};

/**
 * Verifica o cache de chats em atendimento e decide se o hita ou invalida
 */
export const fetchChatsInAttendanceByLogin = createAsyncThunk(
  ACTION_PREFIX_FETCH_BY_LOGIN,
  async ({ login }, { getState, rejectWithValue }) => {
    if (!login) {
      return rejectWithValue('O login do atendente não foi fornecido.');
    }

    const { [CHATS_BY_AGENT_LOGIN_SLICE_NAME]: chatsState } = getState();
    const { loginMap, cacheTimestamps, cacheTimeInSeconds } = chatsState;

    const now = Date.now();
    const lastFetchTime = cacheTimestamps[login];
    const isCacheValid =
      lastFetchTime && (now - lastFetchTime) / 1000 < cacheTimeInSeconds;

    if (isCacheValid && loginMap[login]) {
      return { login, data: loginMap[login], fromCache: true };
    }

    return fetchAndValidateChats(login, rejectWithValue);
  }
);

/**
 * Força uma busca dos chats em atendimento para um login independente do cache
 */
export const forceFetchChatsInAttendanceByLogin = createAsyncThunk(
  ACTION_PREFIX_FORCE_FETCH_BY_LOGIN,
  async ({ login }, { rejectWithValue }) => {
    if (!login) {
      return rejectWithValue('O login do atendente não foi fornecido.');
    }
    return fetchAndValidateChats(login, rejectWithValue);
  }
);

/**
 * @typedef {object} AgentChats
 * @property {Array} inAttendance - Chats que estão em atendimento ativo. Na tela de atendimento, estão na aba "Em atendimento".
 * A condição técnica que define este estado é { situacao = 'em_atendimento' }.
 *
 * @property {Array} attributed - Chats que foram atribuídos a um agente específico, mas que ainda não foram ativamente atendidos. Ficam em uma "Atribuído", na tela de atendimento.
 * A condição técnica que define este estado é o status do chat ser `assigned`.
 *
 * @property {Array} inWait - Chats que estão em espera em uma fila. Aba "Em espera" do atendimento.
 * As condições técnicas para este estado são: a propriedade `isOnHold` do chat ser `true`, ou o seu status ser `notAnswered`.
 */

/**
 * @typedef {Object.<string, AgentChats>} LoginMap
 * O loginMap armazena os chats de cada agente, categorizados por estado.
 * A chave é o login do agente.
 */
const initialState = {
  isLoading: false,
  error: null,
  /** @type {LoginMap} */
  loginMap: {},
  cacheTimestamps: {},
  cacheTimeInSeconds: 60
};

/**
 * Armazena os chats de um atendente de acordo com o estado desse chat. Também expõe ações para manipular esse estado.
 */
const chatsByAgentLoginSlice = createSlice({
  name: CHATS_BY_AGENT_LOGIN_SLICE_NAME,
  initialState,
  reducers: {
    /**
     * Adiciona um novo chat à lista de chats em atendimento de um agente.
     * O chat é validado contra o schema antes de ser adicionado.
     * @param {object} state - O estado atual do slice.
     * @param {object} action - A ação despachada.
     * @param {object} action.payload - O payload da ação.
     * @param {string} action.payload.login - O login do agente.
     * @param {object} action.payload.chat - O objeto do chat a ser adicionado.
     */
    addChatInAttendanceToLogin(state, action) {
      const { login, chat } = action.payload;

      if (!login || !chat) {
        console.error(
          'addChatInAttendanceToLogin requer um login e um objeto de chat no payload.'
        );
        return;
      }

      const { success, error } =
        CHAT_IN_ATTENDANCE_SCHEMA.safeValidateSync(chat);

      if (!success) {
        console.error(
          'Falha ao validar o chat a ser adicionado:',
          error?.message
        );
        return;
      }

      // Garante que o objeto para o login exista no mapa
      if (!state.loginMap[login]) {
        state.loginMap[login] = {
          inAttendance: [],
          attributed: [],
          inWait: []
        };
      }

      // Evita adicionar chats duplicados
      const chatExists = state.loginMap[login].inAttendance.some(
        (existingChat) => existingChat.id === chat.id
      );

      if (!chatExists) {
        state.loginMap[login].inAttendance.push(chat);
      }
    },
    invalidateCacheByLogin(state, action) {
      const login = action.payload;
      if (login && state.cacheTimestamps[login]) {
        delete state.cacheTimestamps[login];
      }
    },
    setCacheTime(state, action) {
      state.cacheTimeInSeconds = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChatsInAttendanceByLogin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(forceFetchChatsInAttendanceByLogin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchChatsInAttendanceByLogin.fulfilled, (state, action) => {
        if (action.payload.fromCache) {
          state.isLoading = false;
          return;
        }

        const { login, data } = action.payload;
        state.loginMap[login] = {
          inAttendance: data,
          attributed: [],
          inWait: []
        };
        state.cacheTimestamps[login] = Date.now();
        state.isLoading = false;
      })
      .addCase(
        forceFetchChatsInAttendanceByLogin.fulfilled,
        (state, action) => {
          const { login, data } = action.payload;
          state.loginMap[login] = {
            inAttendance: data,
            attributed: [],
            inWait: []
          };
          state.cacheTimestamps[login] = Date.now();
          state.isLoading = false;
        }
      )
      .addCase(fetchChatsInAttendanceByLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(forceFetchChatsInAttendanceByLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  }
});

export const {
  addChatInAttendanceToLogin,
  invalidateCacheByLogin,
  setCacheTime
} = chatsByAgentLoginSlice.actions;

export default chatsByAgentLoginSlice.reducer;
